(() => {
  if (customElements.get('eb-trust-row-carousel')) {
    return;
  }

  customElements.define(
    'eb-trust-row-carousel',
    class EbTrustRowCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-eb-trust-row-track]');
        this.cards = this.track ? Array.from(this.track.children) : [];

        if (!this.track || this.cards.length < 2) {
          return;
        }

        this.index = 0;
        this.timer = null;
        this.mobileQuery = window.matchMedia('(max-width: 1120px)');
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.handleStateChange = this.handleStateChange.bind(this);
        this.handleVisibility = this.handleVisibility.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(this.handleStateChange) : null;

        this.mobileQuery.addEventListener('change', this.handleStateChange);
        this.motionQuery.addEventListener('change', this.handleStateChange);
        document.addEventListener('visibilitychange', this.handleVisibility);
        this.resizeObserver?.observe(this);

        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.track.addEventListener('pointerdown', () => this.stop(), { passive: true });
        this.track.addEventListener('pointerup', () => this.start(), { passive: true });
        this.track.addEventListener('pointercancel', () => this.start(), { passive: true });
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());

        this.syncLayout();
        this.start();
      }

      disconnectedCallback() {
        this.stop();

        if (this.mobileQuery) {
          this.mobileQuery.removeEventListener('change', this.handleStateChange);
        }

        if (this.motionQuery) {
          this.motionQuery.removeEventListener('change', this.handleStateChange);
        }

        document.removeEventListener('visibilitychange', this.handleVisibility);
        this.resizeObserver?.disconnect();
      }

      shouldRun() {
        const isNarrow = this.isNarrow();

        return isNarrow && !this.motionQuery.matches && !document.hidden;
      }

      isNarrow() {
        // Match the CSS container query: the carousel layout activates when
        // the section's own width is narrow, regardless of viewport width.
        // This keeps the auto-rotation in sync with the layout in the Shopify
        // theme editor's device preview, where the viewport stays wide.
        return this.getBoundingClientRect().width <= 1120;
      }

      syncLayout() {
        this.track.classList.toggle('is-mobile-carousel', this.isNarrow());
      }

      start() {
        this.stop();
        this.syncLayout();

        if (!this.shouldRun()) {
          return;
        }

        this.timer = window.setInterval(() => {
          this.goTo(this.index + 1);
        }, 3600);
      }

      stop() {
        if (!this.timer) {
          return;
        }

        window.clearInterval(this.timer);
        this.timer = null;
      }

      goTo(nextIndex) {
        this.cards = Array.from(this.track.children);

        if (!this.cards.length) {
          return;
        }

        this.index = nextIndex % this.cards.length;

        if (this.index < 0) {
          this.index = this.cards.length - 1;
        }

        const card = this.cards[this.index];
        const centeredLeft = card.offsetLeft - (this.track.clientWidth - card.clientWidth) / 2;

        this.track.scrollTo({
          left: centeredLeft,
          behavior: 'smooth'
        });
      }

      handleStateChange() {
        this.syncLayout();
        this.start();
      }

      handleVisibility() {
        if (document.hidden) {
          this.stop();
          return;
        }

        this.start();
      }

      handleScroll() {
        if (this.scrollFrame) {
          window.cancelAnimationFrame(this.scrollFrame);
        }

        this.scrollFrame = window.requestAnimationFrame(() => {
          const viewportCenter = this.track.scrollLeft + this.track.clientWidth / 2;
          let closestIndex = 0;
          let closestDistance = Infinity;

          this.cards.forEach((card, cardIndex) => {
            const cardCenter = card.offsetLeft + card.clientWidth / 2;
            const distance = Math.abs(viewportCenter - cardCenter);

            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = cardIndex;
            }
          });

          this.index = closestIndex;
        });
      }
    }
  );
})();
